import { useCallback, useRef, useState } from 'react';

/**
 * Manages the exit-animation lifecycle for a ToolbarActionMenu.
 *
 * Instead of immediately unmounting the component when the consumer sets
 * visibility to `false`, this hook introduces an `isClosing` state so the
 * exit animation can play before the component is removed from the DOM.
 *
 * Usage:
 * ```tsx
 * const { isRendered, isClosing, requestClose, onExitComplete } = useToolbarExitAnimation(isOpen);
 * if (!isRendered) return null;
 * return <ToolbarActionMenu isClosing={isClosing} onExitComplete={onExitComplete} ... />;
 * ```
 */
export const useToolbarExitAnimation = (isOpen: boolean) => {
  const [isClosing, setIsClosing] = useState(false);
  const wasOpenRef = useRef(false);

  // Determine whether the component should still be in the DOM.
  // It's rendered when open OR while the close animation is playing.
  const isRendered = isOpen || isClosing;

  // Track open → close transition. When `isOpen` flips to false while we
  // were previously open, start the closing animation.
  if (wasOpenRef.current && !isOpen && !isClosing) {
    setIsClosing(true);
  }
  wasOpenRef.current = isOpen;

  const onExitComplete = useCallback(() => {
    setIsClosing(false);
  }, []);

  /**
   * Programmatic close trigger — equivalent to setting `isOpen` to false
   * but can be used where the caller controls close via a callback (e.g.
   * the toolbar's own close button) rather than a parent-driven prop.
   */
  const requestClose = useCallback(() => {
    setIsClosing(true);
  }, []);

  return { isRendered, isClosing, requestClose, onExitComplete };
};
